/* eslint-disable clubmed/no-fake-component */
import React, { useContext, useEffect, useMemo } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { useFormikContext } from 'formik';
import { get } from 'lodash';
import { useSelector } from 'react-redux';
import { t } from 'Src/i18n';
import {
  shouldHideField,
  isChildWillBeAdultByArrivalDate,
  getValidators,
  getAdultCivilities,
  getChildCivilities,
} from 'Components/widgets/contact-info/ContactInfoUtils';
import { useAppContext } from 'Context/ReactAppContext';
import { getCurrentProposal } from 'Store/booking/booking.selector';
import type { State as userInfoType } from 'Store/user-info/reducers/userInfoReducer';
import 'Store/user-info/reducers/userInfoReducer';
import { FormikInput } from 'Components/frameworks/formik/FormikInput';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { OptinsContext } from 'Context/OptinsProvider';
import { useGQLErrorsHandler } from 'Utils/use-gql-errors-handler/useGQLErrorsHandler';
import { useGQL } from 'Utils/graphql-client/use-gql/useGQL';
import { getCivilitiesQuery } from 'Views/account/profile/ProfileView.queries';
import { LegalInfo } from './LegalInfo';
import { AdditionalContactInfos } from './AdditionalContactInfos';
import { Subscriptions } from './Subscriptions';
import { UnicodeFields } from './UnicodeFields';
import { CivilityInputBlock } from './CivilityInputBlock';
import type { CivilitiesType } from './PersonInfoUtils';
import { onKeyDownDataForm, getFieldVisibilities } from './PersonInfoUtils';
type Props = {
  arrivalDate?: string;
  addressSchema: Array<ValidationSchema>;
  attendeeSchema: Array<ValidationSchema>;
  attendeeIdentifier: string;
  connectedUserInfo?: userInfoType;
  hasAddress?: boolean;
  i18n: I18N;
  id: string;
  isChild?: boolean;
  isMain?: boolean;
  hideErrorMessage?: boolean;
  isDisabled?: boolean;
};
export const PersonInfoFormContent = ({
  arrivalDate,
  addressSchema,
  attendeeSchema,
  attendeeIdentifier,
  connectedUserInfo,
  hasAddress,
  i18n,
  id,
  isChild,
  isMain,
  hideErrorMessage,
  isDisabled,
}: Props) => {
  const appContext = useAppContext();
  const optinsContext = useContext(OptinsContext);
  const formik = useFormikContext();
  const { isUserLogged } = appContext;
  const selectedProposal = useSelector(getCurrentProposal);
  const isLastnameFirst = isFeatureFlipped(appContext)('booking.lastnameFirst');
  const { data: civilitiesData, error: civilitiesError } = useGQL({
    query: getCivilitiesQuery(),
    queryOptions: {
      cacheFront: {
        key: 'REQUEST_CIVILITIES',
      },
      requestName: 'REQUEST_CIVILITIES',
      locale: connectedUserInfo?.locale,
    },
    cache: false,
    map: (res) => res.civilities,
  });
  useGQLErrorsHandler(civilitiesError, {
    catchAuthError: true,
  });
  const adultCivilities: CivilitiesType = getAdultCivilities(
    civilitiesData?.adult,
    appContext.locale,
  );
  const childCivilities: CivilitiesType = getChildCivilities(
    civilitiesData?.child,
    appContext.locale,
  );
  const validators = useMemo(
    () =>
      getValidators({
        arrivalDate,
        attendeeSchema,
        addressSchema,
        locale: appContext.locale,
        adultCivilities: adultCivilities.map(({ value }) => value),
        childCivilities: childCivilities.map(({ value }) => value),
      }),
    [attendeeSchema, adultCivilities, childCivilities],
  );
  const {
    civilityField,
    firstNameField,
    lastNameField,
    unicodeFirstNameField,
    unicodeLastNameField,
    emailField,
    birthdateField,
    phonesField,
    sanitaryIdField,
    documentFieldsVisibilities,
    shouldDisplayLegalInfo,
  } = getFieldVisibilities(
    attendeeSchema,
    appContext.locale,
    arrivalDate,
    selectedProposal.departureDate,
  );
  const adult = formik.values?.[0]?.adults?.[0];
  useEffect(() => {
    if (
      adult?.optinEmail !== undefined ||
      adult?.optinMail !== undefined ||
      adult?.optinPhone !== undefined ||
      adult?.optinSms !== undefined ||
      adult?.optinClubmedPlus !== undefined
    ) {
      optinsContext.setOptins({
        ...optinsContext.optins,
        optinEmail: Boolean(formik.values[0].adults[0].optinEmail),
        optinMail: Boolean(formik.values[0].adults[0].optinMail),
        optinPhone: Boolean(formik.values[0].adults[0].optinPhone),
        optinSms: Boolean(formik.values[0].adults[0].optinSms),
        optinClubmedPlus: Boolean(formik.values[0].adults[0].optinClubmedPlus),
      });
    }
  }, [adult]);
  const attendeeBirthdate = get(formik.values, `${attendeeIdentifier}.birthdate`);
  const isChildToBeAdult =
    arrivalDate && isChildWillBeAdultByArrivalDate(attendeeBirthdate, arrivalDate);
  const dataForm = `PersonInfo-${id}`;
  const onKeyDown = onKeyDownDataForm(dataForm);
  const shouldDisplayUnicodeFields =
    unicodeFirstNameField.isVisible || unicodeLastNameField.isVisible;
  const {
    idcardSecondName,
    idcardThirdName,
    idcardFourthName,
    passportSecondName,
    passportThirdName,
    passportFourthName,
  } = documentFieldsVisibilities;
  const firstNameInputs = (
    <FormikInput
      autoComplete="given-name"
      hideErrorMessage={hideErrorMessage}
      isDisabled={
        isDisabled ||
        shouldHideField({
          connectedUserInfo,
          isChild,
          attendeeIdentifier,
          fieldName: 'firstName',
          formInitialValues: formik.initialValues,
          isMain,
        }) ||
        (isMain && isUserLogged)
      }
      isRequired={firstNameField.isRequired}
      label={t('booking.participants.labels.firstName')}
      labelAdditionalInfo={t('common.form.notes.matchingPassport')}
      name={`${attendeeIdentifier}.firstName`}
      onKeyDown={onKeyDown}
      type="text"
      validate={validators.firstName}
    />
  );
  const lastNameInputs = (
    <FormikInput
      autoComplete="family-name"
      hideErrorMessage={hideErrorMessage}
      isDisabled={
        isDisabled ||
        shouldHideField({
          connectedUserInfo,
          isChild,
          fieldName: 'lastName',
          attendeeIdentifier,
          formInitialValues: formik.initialValues,
          isMain,
        }) ||
        (isMain && isUserLogged)
      }
      isRequired={lastNameField.isRequired}
      label={t('booking.participants.labels.lastName')}
      labelAdditionalInfo={t('common.form.notes.matchingPassport')}
      name={`${attendeeIdentifier}.lastName`}
      onKeyDown={onKeyDown}
      type="text"
      validate={validators.lastName}
    />
  );

  const renderSecondName = (secondName) => {
    return (
      secondName?.isVisible && (
        <div className="p-10 sm:w-1/2">
          <FormikInput
            dataTestId="documentSecondName_input"
            hideErrorMessage={hideErrorMessage}
            isRequired={secondName.isRequired}
            label={secondName.label}
            name={`${attendeeIdentifier}.document.${secondName.type}.secondName`}
            onClick={onKeyDown}
            type="text"
            validate={secondName.validator}
          />
        </div>
      )
    );
  };

  const renderThirdName = (thirdName) => {
    return (
      thirdName?.isVisible && (
        <div className="p-10 sm:w-1/2">
          <FormikInput
            dataTestId="documentSecondName_input"
            hideErrorMessage={hideErrorMessage}
            isRequired={thirdName.isRequired}
            label={thirdName.label}
            name={`${attendeeIdentifier}.document.${thirdName.type}.thirdName`}
            onClick={onKeyDown}
            type="text"
            validate={thirdName.validator}
          />
        </div>
      )
    );
  };

  const renderFourthName = (fourthName) => {
    return (
      fourthName?.isVisible && (
        <div className="p-10 sm:w-1/2">
          <FormikInput
            dataTestId="documentSecondName_input"
            hideErrorMessage={hideErrorMessage}
            isRequired={fourthName.isRequired}
            label={fourthName.label}
            name={`${attendeeIdentifier}.document.${fourthName.type}.fourthName`}
            onClick={onKeyDown}
            type="text"
            validate={fourthName.validator}
          />
        </div>
      )
    );
  };

  return (
    <div data-testid={isMain ? 'main-participant-form' : 'other-participant-form'}>
      <p className="start-0 mx-20 mt-5 text-right text-12 text-black sm:absolute">
        {t('common.form.notes.mandatoryFields')}
      </p>
      <form data-form={dataForm}>
        <fieldset className="flex flex-col p-10 pt-5 sm:flex-row sm:flex-wrap sm:pt-10">
          {civilityField.isVisible && (
            <div className="-mx-5 w-full py-10">
              <CivilityInputBlock
                adultCivilities={adultCivilities}
                attendeeIdentifier={attendeeIdentifier}
                childCivilities={childCivilities}
                isChild={!!isChild}
                isDisabled={isDisabled || (isMain && isUserLogged)}
                validators={validators}
              />
            </div>
          )}
          {(isLastnameFirst ? lastNameField : firstNameField).isVisible && (
            <div className="p-10 sm:w-1/2">
              {isLastnameFirst ? lastNameInputs : firstNameInputs}
            </div>
          )}

          {(isLastnameFirst ? firstNameField : lastNameField).isVisible && (
            <div className="p-10 sm:w-1/2">
              {isLastnameFirst ? firstNameInputs : lastNameInputs}
            </div>
          )}

          {renderSecondName(idcardSecondName)}
          {renderSecondName(passportSecondName)}
          {renderThirdName(idcardThirdName)}
          {renderThirdName(passportThirdName)}
          {renderFourthName(idcardFourthName)}
          {renderFourthName(passportFourthName)}

          {shouldDisplayUnicodeFields && (
            <UnicodeFields
              attendeeIdentifier={attendeeIdentifier}
              connectedUserInfo={connectedUserInfo}
              onKeyDown={onKeyDown}
              validators={validators}
              isUnicodeFirstNameVisible={unicodeFirstNameField.isVisible}
              isUnicodeFirstNameRequired={unicodeFirstNameField.isRequired}
              isUnicodeLastNameVisible={unicodeLastNameField.isVisible}
              isUnicodeLastNameRequired={unicodeLastNameField.isRequired}
              isMain={isMain}
              isChild={isChild}
            />
          )}

          {birthdateField.isVisible && (
            <div
              className={classnames('p-10 sm:w-full', {
                'order-first': isChild && !isChildToBeAdult,
              })}
            >
              <FormikInput
                dateFormat={t('booking.form.dateFormat')}
                dayPlaceholder={t('booking.form.dayPlaceholder')}
                isDisabled={isDisabled || (isChild && !isChildToBeAdult)}
                hideErrorMessage={hideErrorMessage}
                isRequired={birthdateField.isRequired}
                label={t('booking.participants.labels.birthdate')}
                labelAdditionalInfo={t('common.form.notes.matchingPassport')}
                monthPlaceholder={t('booking.form.monthPlaceholder')}
                name={`${attendeeIdentifier}.birthdate`}
                onKeyDown={onKeyDown}
                type="birthdate"
                validate={isChild ? validators.childBirthDate : validators.adultBirthDate}
                yearPlaceholder={t('booking.form.yearPlaceholder')}
                errorMessage={
                  !attendeeBirthdate && t('booking.participants.errorMessage.birthdate')
                }
              />
            </div>
          )}
        </fieldset>

        <AdditionalContactInfos
          addressSchema={addressSchema}
          attendeeIdentifier={attendeeIdentifier}
          connectedUserInfo={connectedUserInfo}
          formik={formik}
          hasAddress={!!hasAddress}
          onKeyDown={onKeyDown}
          validators={validators}
          isMain={!!isMain}
          isChild={!!isChild}
          isEmailVisible={emailField.isVisible}
          isEmailRequired={emailField.isRequired}
          isPhonesVisible={phonesField.isVisible}
          isPhonesRequired={phonesField.isRequired}
          hideErrorMessage={hideErrorMessage}
        />

        {sanitaryIdField.isVisible && (
          <fieldset className="mt-10 p-10 sm:flex sm:flex-wrap">
            <div className="p-10 sm:w-1/2">
              <FormikInput
                hideErrorMessage={hideErrorMessage}
                isDisabled={false}
                isRequired={sanitaryIdField.isRequired}
                label={t('booking.participants.labels.sanitaryPass')}
                name={`${attendeeIdentifier}.healthPassId`}
                onKeyDown={onKeyDown}
                type="text"
                validate={validators.healthPassId}
              />
            </div>
          </fieldset>
        )}
        {shouldDisplayLegalInfo && (
          <LegalInfo
            attendeeIdentifier={attendeeIdentifier}
            dateFormat={t('booking.form.dateFormat')}
            dayPlaceholder={t('booking.form.dayPlaceholder')}
            documentFieldsVisibilities={documentFieldsVisibilities}
            hideErrorMessage={hideErrorMessage}
            monthPlaceholder={t('booking.form.monthPlaceholder')}
            onKeyDown={onKeyDown}
            yearPlaceholder={t('booking.form.yearPlaceholder')}
          />
        )}
        {isMain && (
          <Subscriptions
            attendeeIdentifier={attendeeIdentifier}
            formik={formik}
            i18n={i18n}
            isSeller={appContext.isSeller}
            data-testid="participant-form-optionout"
          />
        )}
      </form>
    </div>
  );
};
